import { useLocation, useNavigate, useParams } from "react-router-dom";
import AuctionDetail from "../../components/AuctionDetail/AuctionDetail";
import AuctionDetailVendorTab from "../../components/AuctionDetailVendorTab/AuctionDetailVendorTab";
import "./AuctionDetailPage.scss";
import React, { useEffect, useRef, useState } from "react";
import { Auction } from "../../models/Auction";
import ApiService from "../../services/apiService";
import URLS from "../../urls/Urls";
import BidHall from "../BidHall/BidHall";
import XetModal from "../../components/shared/xet-modal/XetModal";
import AuctionEndDialog from "../../components/AuctionEndDialog/AuctionEndDialog";
import AuctionBidResult from "../../components/AuctionBidResult/AuctionBidResult";
import BidHistoryResult from "../../components/BidHistoryResult/BidHistoryResult";

const AuctionDetailPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [timeLeft, setTimeLeft] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [auction, setAuction] = useState<Auction>();
  const { id } = useParams();
  const [showAuctionEndedDialog, setShowAuctionEndedDialog] = useState(false);
  const [showAuctionLiveEndedDialog, setShowAuctionLiveEndedDialog] =
    useState(false);
  const [wasAuctionLive, setWasAuctionLive] = useState(false);
  const [wasAuctionActive, setWasAuctionActive] = useState(false);
  const [liveBidHall, setLiveBidHall] = useState(true);
  const [participants, setParticipants] = useState("");
  const [bidders, setBidders] = useState("");

  useEffect(() => {
    fetchAuctionDetails();
  }, [id]);

  const fetchAuctionDetails = async () => {
    try {
      const { data } = await ApiService().client.get(
        URLS.AUCTION.DETAILS(id || ""),
      );
      if (data) {
        setAuction(data.auction);
        setParticipants(data.participants);
        setBidders(data.bidders);
        const startTime = new Date(data.auction.start_time).getTime();
        const endTime = new Date(data.auction.end_time).getTime();
        const now = new Date().getTime();
        setWasAuctionActive(endTime > now);
        setWasAuctionLive(startTime > now);
      }
    } catch (e) {
      console.error("Error:", e);
    }
  };

  useEffect(() => {
    if (location.pathname.endsWith("vendors")) {
      setActiveTab(1);
    } else if (location.pathname.endsWith("bid-hall")) {
      setActiveTab(2);
    } else {
      setActiveTab(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const updateTimer = () => {
      if (auction) {
        const endTime = new Date(auction.end_time).getTime();
        const liveTime = new Date(auction.start_time).getTime();
        const now = new Date().getTime();

        const pastTimeDifference = endTime - now;
        const liveTimeDifference = liveTime - now;

        const formatTime = (timeDifference: any) => {
          const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
          const hours = Math.floor(
            (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          );
          const minutes = Math.floor(
            (timeDifference % (1000 * 60 * 60)) / (1000 * 60),
          );
          const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);
          return `${days}d ${hours}h ${minutes}m ${seconds}s`;
        };

        if (liveTimeDifference > 0) {
          setTimeLeft(`Live in ${formatTime(liveTimeDifference)}`);
        } else if (pastTimeDifference > 0) {
          setTimeLeft(`${formatTime(pastTimeDifference)} Left`);
          if (wasAuctionLive) {
            setShowAuctionLiveEndedDialog(true);
          }
        } else {
          setTimeLeft("Auction Ended");
          if (wasAuctionActive) {
            setShowAuctionEndedDialog(true);
          }
        }
      }
    };

    const intervalId = setInterval(updateTimer, 1000);
    updateTimer();

    return () => clearInterval(intervalId);
  }, [auction, wasAuctionActive]);

  const handleAuctionEnded = () => {
    setShowAuctionLiveEndedDialog(false);
    setShowAuctionEndedDialog(false);
    setWasAuctionActive(false);
    setWasAuctionLive(false);
    window.location.reload();
  };

  return (
    <div className="auction-detail-main-page full-width flex-column">
      <div className="upper-bar flex-row align-items-center justify-content-space-between py-12 px-16">
        <p className="xetgo-font-tag">
          <span
            className="sub-title cursor-pointer"
            onClick={() => navigate("/auctions")}
          >
            Auctions /
          </span>{" "}
          <span
            className={`${activeTab === 1 && "sub-title"} cursor-pointer`}
            onClick={() => navigate(`/auctions/${id}`)}
          >
            {auction?.title}
          </span>
          {activeTab === 1 && <span className="sub-title"> / </span>}
          {activeTab === 1 && <span> Vendors </span>}
        </p>
      </div>
      <div className="auction-detail-lower-main-container px-16 py-20 flex-column gap-12">
        <div className="auction-detail-tab-main-container flex-row align-items-center justify-content-center xetgo-font-button">
          <div
            onClick={() => navigate(`/auctions/${id}`)}
            className={`tab-container cursor-pointer border-box flex-row align-items-center gap-8 px-12 py-8 flex-row align-items-center justify-content-center ${
              activeTab == 0 && "activeTab"
            }`}
          >
            <img
              height={18}
              width={18}
              src={`${
                activeTab == 0
                  ? "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241122102123-Group+1171276134.svg"
                  : "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241122102353-Group+1171276134.svg"
              }`}
              alt="auction-icon"
            />
            <p>Auction Details</p>
          </div>
          <div
            onClick={() => navigate(`/auctions/${id}/vendors`)}
            className={`tab-container border-box flex-row align-items-center gap-8 px-12 py-8 flex-row align-items-center justify-content-center        cursor-pointer ${
              activeTab == 1 && "activeTab"
            }`}
          >
            <img
              height={18}
              width={18}
              src={`${
                activeTab == 1
                  ? "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241122103346-Icon.svg"
                  : "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241122103512-Icon.svg"
              }`}
              alt="auction-icon"
            />
            <p>Manage Bidders</p>
          </div>
          <div
            onClick={() => navigate(`/auctions/${id}/bid-hall`)}
            className={`tab-container cursor-pointer border-box flex-row align-items-center gap-8 px-12 py-8 flex-row align-items-center justify-content-center ${
              activeTab == 2 && "activeTab"
            }`}
          >
            <img
              height={18}
              width={18}
              src={`${
                activeTab == 2
                  ? "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241122102123-Group+1171276134.svg"
                  : "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241122102353-Group+1171276134.svg"
              }`}
              alt="auction-icon"
            />
            <p>Bid Hall</p>
          </div>
        </div>

        {auction && auction.status === "UPCOMING" && (
          <div className="flex-row align-items-center justify-content-space-between xetgo-font-tag">
            <p className="public-crumb px-24 py-6">Public</p>
            <p
              onClick={() => navigate(`/auctions/${auction.id}/edit`)}
              className="edit-auction-btn px-16 py-7 cursor-pointer"
            >
              Edit Auction
            </p>
          </div>
        )}

        {auction && (
          <div className="bid-hall-upper-bar xetgo-font-tag p-12 flex-row justify-content-space-between">
            <p className="flex-row gap-16 title-green">
              <span className=" bold">Participants </span>{" "}
              <span className="sub-title-green">{participants}</span>
            </p>
            <p className="flex-row gap-16  title-green">
              <span className=" bold">Active Bidders </span>{" "}
              <span className="sub-title-green">{bidders}</span>
            </p>

            <p style={{ width: "160px" }} className="title-brown bolder">
              {timeLeft}
            </p>
            {auction.status === "PAST" && (
              <div style={{ width: "150px", height: "16px" }}>
                {liveBidHall ? (
                  <AuctionBidResult auction={auction} />
                ) : (
                  <BidHistoryResult auction={auction} />
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-scroll">
          {activeTab === 0 && auction && <AuctionDetail auction={auction} />}
          {activeTab === 1 && auction && <AuctionDetailVendorTab />}
          {activeTab === 2 && auction && (
            <BidHall
              auction={auction}
              liveBidHall={liveBidHall}
              setLiveBidHall={setLiveBidHall}
            />
          )}
          {auction && (
            <XetModal
              backdropClose={false}
              open={showAuctionEndedDialog}
              handleClose={() => setShowAuctionEndedDialog(false)}
            >
              <AuctionEndDialog
                onSubmit={handleAuctionEnded}
                startTime={auction.start_time}
                endTime={auction.end_time}
                title={auction.title}
                id={auction.auction_id}
                heading={"Ended"}
              />
            </XetModal>
          )}
          {auction && (
            <XetModal
              backdropClose={false}
              open={showAuctionLiveEndedDialog}
              handleClose={() => setShowAuctionLiveEndedDialog(false)}
            >
              <AuctionEndDialog
                onSubmit={handleAuctionEnded}
                startTime={auction.start_time}
                endTime={auction.end_time}
                title={auction.title}
                id={auction.auction_id}
                heading={"Is Live"}
              />
            </XetModal>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuctionDetailPage;
